import { useEffect, useState } from 'react';
import { Drawer, Box, Stack, Typography, Chip, IconButton, Button, TextField, Divider,
         Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, useMediaQuery,
         Checkbox, FormControlLabel } from '@mui/material';
import { Close, ContentCopy, Streetview, Place, Delete, Save, OpenInNew, CheckCircle, ErrorOutline } from '@mui/icons-material';
import { wsurl, putToDB, delToDB } from '../Common';
import { parseCartellone, votoColor, votoLabel, safeHttpUrl } from '../lib/parse';
import ZoomableImage from './ZoomableImage';

export default function DetailPanel({ item, onClose, onSaved, notify, selected, setSelected }) {
  const raw = item?.raw;
  const isMobile = useMediaQuery('(max-width:700px)');
  const [photoIdx, setPhotoIdx] = useState(0);
  const [title, setTitle] = useState('');
  const [via, setVia] = useState('');
  const [sw, setSw] = useState('');
  const [voto, setVoto] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (raw) {
      setTitle(raw.title || ''); setVia(raw.nomination || ''); setSw(raw.urlstreeview || '');
      setVoto(parseCartellone(raw).voto);   // voto effettivo: campo a DB o dedotto dal titolo
      setPhotoIdx(0); setConfirmDel(false);
    }
  }, [raw?.id]);

  if (!raw) return null;
  // meta live sui campi in modifica: lo stato diventa "revisionato" appena via + Street View sono compilati
  const meta = parseCartellone({ ...raw, title, voto, nomination: via, urlstreeview: sw });
  const vc = votoColor(meta.voto);
  const photos = [
    { src: `${wsurl}/image/${raw.id}`, label: 'principale' },
    ...(raw.objimgcntx || []).map(cid => ({ src: `${wsurl}/cntx/${cid}`, label: `contesto ${cid}` })),
  ];
  const mapsUrl = `https://maps.google.com/maps?q=${raw.latitude},${raw.longitude}`;
  const swUrl = safeHttpUrl(sw) || `https://maps.google.com/maps?q=&layer=c&cbll=${raw.latitude},${raw.longitude}`;

  const salva = () => {
    const aggiornato = { ...raw, title, nomination: via, urlstreeview: sw || null, voto };
    putToDB(aggiornato, setBusy, () => notify('Errore nel salvataggio delle modifiche', 'error'), () => {
      notify('Modifiche salvate ✓', 'success');
      onSaved();
    });
  };
  const elimina = () => {
    delToDB(raw, setBusy, () => notify("Errore durante l'eliminazione", 'error'), () => {
      notify(`Cartellone ${raw.id} eliminato`, 'info');
      setConfirmDel(false);
      onSaved();
      onClose();
    });
  };

  return (
    <Drawer anchor="right" open={!!raw} onClose={onClose}
            PaperProps={{ sx: { width: isMobile ? '100%' : 'min(720px, 62vw)', maxWidth: '100vw' } }}>
      <Stack sx={{ p: 2, gap: 1.2, overflowY: 'auto' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption" color="text.secondary">ID {String(raw.id).padStart(7, '0')}</Typography>
          <Box flex={1} />
          <Tooltip title="Aggiungi/togli dalla selezione per l'export">
            <FormControlLabel sx={{ mr: 0.5, '& .MuiFormControlLabel-label': { fontSize: 13, fontWeight: 600, color: '#475569' } }}
              control={<Checkbox size="small" checked={!!selected?.has(raw.id)}
                                 onChange={(e) => setSelected?.(prev => {
                                   const next = new Set(prev);
                                   e.target.checked ? next.add(raw.id) : next.delete(raw.id);
                                   return next;
                                 })} />}
              label="Seleziona" />
          </Tooltip>
          {meta.stato === 'revisionato'
            ? <Chip size="small" icon={<CheckCircle />} label="Revisionato" sx={{ bgcolor: '#ecfdf5', color: '#15803d', '& .MuiChip-icon': { color: 'inherit' } }} />
            : <Chip size="small" icon={<ErrorOutline />} label="Da rivedere" sx={{ bgcolor: '#fff7ed', color: '#c2410c', '& .MuiChip-icon': { color: 'inherit' } }} />}
          <IconButton onClick={onClose}><Close /></IconButton>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {meta.concessionaria && <Chip variant="outlined" label={meta.concessionaria} sx={{ color: '#1d4ed8', borderColor: '#bfdbfe', fontWeight: 700 }} />}
          <Chip label={votoLabel(meta.voto)} sx={{ bgcolor: vc.bg, color: vc.fg, fontWeight: 800, fontSize: 16 }} />
        </Stack>
        <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{raw.title || `Cartellone ${raw.id}`}</Typography>
        {raw.nomination && <Typography variant="body2" color="text.secondary">{raw.nomination}</Typography>}
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {meta.tags.map(t => <Chip key={t} size="small" variant="outlined" label={t} sx={{ color: '#64748b' }} />)}
        </Stack>

        <ZoomableImage src={photos[photoIdx].src}
                       alt={`${raw.title || `Cartellone ${raw.id}`} — ${photos[photoIdx].label}`}
                       height={isMobile ? '46vh' : '60vh'}
                       counter={`${photoIdx + 1} / ${photos.length}`} />
        {photos.length > 1 && (
          <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5, flexShrink: 0 }}>
            {photos.map((p, i) => (
              <img key={p.src} src={p.src} alt={p.label} onClick={() => setPhotoIdx(i)}
                   style={{ width: 130, height: 96, objectFit: 'cover', borderRadius: 8, cursor: 'pointer', flexShrink: 0,
                            outline: i === photoIdx ? '3px solid #1d4ed8' : '1px solid #e2e8f0',
                            opacity: i === photoIdx ? 1 : 0.75 }} />
            ))}
          </Stack>
        )}

        <Paper2>
          <Row label="GPS">
            <Typography variant="body2">{raw.latitude?.toFixed(6)}, {raw.longitude?.toFixed(6)}</Typography>
            <Tooltip title="Copia coordinate">
              <IconButton size="small" onClick={() => { navigator.clipboard?.writeText(`${raw.latitude}, ${raw.longitude}`); notify('Coordinate copiate', 'success'); }}>
                <ContentCopy fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Row>
          <Divider />
          <Row label="Street View">
            <Button size="small" startIcon={<Streetview />} endIcon={<OpenInNew />} onClick={() => window.open(swUrl, '_blank', 'noopener,noreferrer')}>
              Guarda su Street View
            </Button>
          </Row>
          <Divider />
          <Row label="Google Maps">
            <Button size="small" startIcon={<Place />} endIcon={<OpenInNew />} onClick={() => window.open(mapsUrl, '_blank', 'noopener,noreferrer')}>
              Apri in Google Maps
            </Button>
          </Row>
          {raw.haveAudioNote && (<>
            <Divider />
            <Row label="Nota vocale">
              <audio controls preload="none" src={`${wsurl}/audionote/${raw.id}`} style={{ height: 34, maxWidth: 240 }} />
            </Row>
          </>)}
        </Paper2>

        <Typography variant="subtitle2" sx={{ mt: 1 }}>✏️ Modifica informazioni</Typography>
        <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mr: 0.5 }}>Voto</Typography>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(v => {
            const c = votoColor(v);
            const on = voto === v;
            return <Chip key={v} label={`V${v}`} size="small" onClick={() => setVoto(on ? null : v)}
                         variant={on ? 'filled' : 'outlined'}
                         sx={{ fontWeight: 800, cursor: 'pointer',
                               ...(on ? { bgcolor: c.bg, color: c.fg, borderColor: c.bg } : { color: '#64748b' }) }} />;
          })}
        </Stack>
        <TextField size="small" label="Titolo" value={title} onChange={e => setTitle(e.target.value)} fullWidth />
        <TextField size="small" label="Via e numero" value={via} onChange={e => setVia(e.target.value)} fullWidth />
        <TextField size="small" label="Link Street View" value={sw} onChange={e => setSw(e.target.value)} fullWidth
                    placeholder={mapsUrl} />

        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button variant="contained" startIcon={<Save />} disabled={busy} onClick={salva} fullWidth>Salva modifiche</Button>
          <Button variant="outlined" onClick={onClose}>Annulla</Button>
        </Stack>
        <Button color="error" variant="outlined" startIcon={<Delete />} disabled={busy}
                onClick={() => setConfirmDel(true)} sx={{ mt: 2 }}>
          Elimina cartellone
        </Button>
      </Stack>

      <Dialog open={confirmDel} onClose={() => setConfirmDel(false)}>
        <DialogTitle>Eliminare il cartellone {raw.id}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">"{raw.title}"<br />L'operazione rimuove anche foto di contesto e nota vocale.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDel(false)}>Annulla</Button>
          <Button color="error" variant="contained" onClick={elimina} disabled={busy}>Elimina definitivamente</Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
}

function Paper2({ children }) {
  return <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, px: 1.5, py: 0.5 }}>{children}</Box>;
}
function Row({ label, children }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.7 }}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 110, flexShrink: 0, fontWeight: 600 }}>{label}</Typography>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, flexWrap: 'wrap' }}>{children}</Stack>
    </Stack>
  );
}
